<template>
    <div>
        <h4>Lugares</h4>

        <div v-if="seatMapLoading">
            Carregando...
        </div>

        <div v-else>
            <div v-if="useDashboardSeatMap">
                <Button 
                    primary
                    :isOutlined="seatsSelected"
                    :onClick="handleShowSeatMap"
                >
                    <span v-if="seatsSelected">
                        Alterar seleção de lugares
                    </span>

                    <span v-else>
                        Selecionar no mapa de lugares
                    </span>
                </Button>
            </div>         

            <div v-else>
                <ReservationResourceSelector
                    :seatMap="seatMap"
                    :currentResourceIds="[]"
                    @onSelectedResources="handleResourceSelectionChange"
                    :allowUnavailable="false"
                />

                <small
                    v-if="helperText"
                    class="form-text"
                    v-bind:class="{ 'invalid-feedback': error, 'text-muted': !error }"
                >
                    {{ helperText }}
                </small>
    
                <div 
                    class="alert alert-warning"
                    role="alert"
                    v-if="value.length && isResourceAmountHigherThanTariffSum"
                >
                    <small>
                        O(s) lugar(es) selecionado(s) comporta(m) um número diferente da quantidade de tarifas selecionadas.
                    </small>
                </div>
            </div>

            <div v-if="seatsSelected">
                <div class="alert alert-success selected-seats-success" role="alert">
                    <div class="selected-seats-header">
                        <i class="material-icons">check_circle</i>
                        <strong>Lugares selecionados com sucesso!</strong>
                    </div>
                </div>
                <div class="selected-seats-collapse-container">
                    <button 
                        type="button" 
                        class="btn btn-sm btn-link collapse-toggle-btn"
                        :aria-expanded="!isSeatsListCollapsed"
                        aria-controls="selected-seats-collapse"
                        @click.prevent="toggleSeatsList"
                    >
                        <i class="material-icons">{{ isSeatsListCollapsed ? 'expand_more' : 'expand_less' }}</i>
                        <span>Ver lugares selecionados</span>
                    </button>

                    <div 
                        :id="'selected-seats-collapse'"
                        class="collapse"
                        :class="{ 'show': !isSeatsListCollapsed }"
                    >
                        <ul class="selected-seats-list">
                            <li 
                                v-for="resource in value" 
                                :key="resource.id" 
                                class="seat-item"
                            >
                                {{ resource.name }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>

import ReservationResourceSelector from '../../../../ReservationResourceSelector';
import Button from '../../../../Button';

import SellerApi from '../../../../../services/SellerApi';

export default {
    components: { ReservationResourceSelector, Button },
    props: {
        appCode: String,
        value: {
            required: true,
        },
        product: {
            required: true,
        },
        scheduling: {
            required: true,
        },
        date: {
            required: true,
        },
        time: {
            required: true,
        },
        duration: {
            required: true,
        },
        tariffs: {
            required: true,
        },
        helperText: {
            type: String,
            required: false,
            default: ''
        },
        error: {
            type: Boolean,
            required: false,
            default: false
        },
        dashboardUrl: {
            type: String,
            required: true,
        },
        useDashboardSeatMap: {
            type: Boolean,
            required: true,
        },
    },
    data(){
        return {
            seatMapLoading: true,
            seatMap: null,
            seatsSelected: false,
            isSeatsListCollapsed: true,
        };
    },
    computed: {
        resourceOptions() { 
            return this.seatMap.areas.reduce((arr, area) => {
                for (let slot of area.slots) {
                    if (slot.type !== 'resource') {
                        continue;
                    }

                    arr = [...arr, slot.resource];
                }

                return arr;
            }, []);
        },
        isDateTimeSellingMode() {
            return this.product?.selling_mode === 'with_date_and_time';
        },
        isDateSellingMode() {
            return this.product?.selling_mode === 'with_date_only';
        },
        defaultTariffGroup(){
            return this.product?.tariff_groups.find(group => group.is_default);
        },
        selectedTariffAmountSum(){
            // retrieve the sum of the selected tariffs amount
            return this.tariffs.reduce(
                (sum, tariff) => sum + tariff.amount,
                0
            );
        },
        isResourceAmountHigherThanTariffSum(){
            const selectedCapacitySum = this.value.reduce(
                (sum, resource) => sum + resource.capacity ?? 0,
                0
            );

            return selectedCapacitySum > this.selectedTariffAmountSum;
        },
        watchedProps() {
            return [this.scheduling.date, this.time?.value, this.duration].join('');
        },
    },
    methods: {
        handleResourceSelectionChange(resources){
            const ResourcesWithCategory = resources.map(resource=>({
                ...resource,
                category: this.seatMap.categories.find(category => category.id === resource.categoryId)
            }));

            this.$emit('input', ResourcesWithCategory)
            this.$emit('change', ResourcesWithCategory)
        },
        getTariffSeatMap(){
            this.seatMapLoading = true;

            const params = { 
                schedule_date: this.scheduling.date,
                schedule_time_minutes: this.time?.value ?? '',
                duration_minutes: this.duration,
            };

            const { id: tariffGroupId } = this.scheduling.finalTariffGroup;

            SellerApi.TariffGroup.seatMap(this.appCode, tariffGroupId, params).then(data => {
                this.seatMapLoading = false;

                this.seatMap = data;
            }).catch(err => {
                this.seatMapLoading = false;
                console.error(err);
            });
        },
        handleShowSeatMap(){
            const message = {
                event_id: "show_seat_map",
                data: {
                    date: this.date.date,
                    timeMinutes: this.time?.value,
                    productId: this.product.id,
                    schedulingId: this.scheduling.id,
                    tariffs: this.tariffs.map(tariff => ({
                        tariffId: tariff.tariff.id,
                        quantity: tariff.amount,
                    })),
                },
            };
            window.parent.postMessage(message, this.dashboardUrl);
        },
        handleSeatMapmessage(event){
            const resources = this.resourceOptions.filter(resource => event.data.data.seatAllocation.selectedResources.some(r => r.resourceSlot.id === resource.id));
            this.handleResourceSelectionChange(resources);
            this.seatsSelected = true;
        },
        toggleSeatsList(){
            this.isSeatsListCollapsed = !this.isSeatsListCollapsed;
        }
    },
    watch: {
        watchedProps() {
            this.getTariffSeatMap();
        },
        tariffs: {
            handler() {
                this.seatsSelected = false;
                this.$emit('input', []);
                this.$emit('change', []);
            },
            deep: true,
        },
    },
    mounted() {
        this.getTariffSeatMap();

        const listener = (event) => {
            if (event?.data?.event_id === "seat_map_selection_finished") {
                this.handleSeatMapmessage(event);
            }
        };

        window.addEventListener("message", listener);
    },
    unmounted() {
        window.removeEventListener('message', this.handleSeatMapMessage.bind(this));
    },
}
</script>

<style scoped lang="scss">
.selected-seats-success {
    margin-top: 1rem;
    padding: 1rem 1.25rem;
    border-radius: 0.375rem;
    
    .selected-seats-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0;
        
        i.material-icons {
            font-size: 1.25rem;
            color: #155724;
        }
        
        strong {
            color: #155724;
            font-size: 0.95rem;
            font-weight: 600;
        }
    }
}

.selected-seats-collapse-container {
    margin-top: 0.5rem;
    
    .collapse-toggle-btn {
        padding: 0.5rem 0;
        color: #000 !important;
        text-decoration: none;
        border: none;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        line-height: 1;
        font-size: 0.875rem;
        
        &:hover, &:focus {
            color: #333 !important;
            text-decoration: none;
            box-shadow: none;
        }
        
        i.material-icons {
            font-size: 1.25rem;
            transition: transform 0.2s ease;
        }
    }
}

.selected-seats-list {
    padding: 0.75rem;
    list-style: none;
    margin: 0;
    
    .seat-item {
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
        font-weight: 500;
        border-radius: 0.25rem;
        color: #333;
        border: 1px solid #333;
        margin-bottom: 0.5rem;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
}
</style>
